/**
 * Animation Library JSON: deterministic serialization plus strict validation on import.
 *
 * Rules: never accept a partially corrupt file — every problem is collected and the whole
 * import is rejected. Round-tripping a library must not change animation behavior.
 */

import { isCanonicalBoneId } from './canonicalRig';
import { normalizeEvents } from './clipOps';
import { isValidQuatTuple, normalizeQuat } from './quat';
import {
  CANONICAL_BONE_IDS,
  DEFAULT_EASING,
  DEFAULT_FOLDER,
  LIBRARY_SCHEMA_VERSION,
  RIG_VERSION,
  TWEEN_PRESETS,
  type AnimationLibraryFile,
  type LibraryFolderMeta,
  type BoneRotationTrack,
  type CanonicalAnimationClip,
  type CanonicalBoneId,
  type ClipEvent,
  type ClipEventData,
  type LibraryParseResult,
  type QuaternionTuple,
  type RotationKeyframe,
  type TweenPreset,
} from './types';

const DECIMALS = 6;
const ROUND_FACTOR = 10 ** DECIMALS;

function round6(n: number): number {
  const r = Math.round(n * ROUND_FACTOR) / ROUND_FACTOR;
  return Object.is(r, -0) ? 0 : r;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isInteger(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && Number.isInteger(v);
}

// ---------------------------------------------------------------------------
// Folder metadata (additive, schema 1)
// ---------------------------------------------------------------------------

/**
 * Folder metadata is cosmetic: a corrupt entry must never cost the user their clips. Both
 * cleaners therefore drop what they cannot use and report how much they dropped, so the caller
 * can turn that into a warning instead of an error.
 */
function cleanFolderNames(raw: unknown): { value: string[]; dropped: number } {
  if (!Array.isArray(raw)) return { value: [], dropped: 0 };
  const value: string[] = [];
  const seen = new Set<string>();
  let dropped = 0;
  for (const entry of raw) {
    if (typeof entry !== 'string' || entry.trim().length === 0) {
      dropped++;
      continue;
    }
    const name = entry.trim();
    // `DEFAULT_FOLDER` is implicit, so listing it is not an error — just nothing to store.
    if (name === DEFAULT_FOLDER || seen.has(name)) continue;
    seen.add(name);
    value.push(name);
  }
  return { value, dropped };
}

function cleanClipFolders(raw: unknown): { value: Record<string, string>; dropped: number } {
  if (!isPlainObject(raw)) return { value: {}, dropped: 0 };
  const value: Record<string, string> = {};
  let dropped = 0;
  for (const key of Object.keys(raw)) {
    if (key.length === 0) {
      dropped++;
      continue;
    }
    const entry: unknown = raw[key];
    if (typeof entry !== 'string' || entry.trim().length === 0) {
      dropped++;
      continue;
    }
    const folder = entry.trim();
    if (folder === DEFAULT_FOLDER) continue;
    value[key] = folder;
  }
  return { value, dropped };
}

// ---------------------------------------------------------------------------
// Serialization
// ---------------------------------------------------------------------------

/**
 * Build a library file from clips. Deterministic: clips sorted by id, tracks emitted in
 * canonical bone order, keyframes sorted by frame, all numbers rounded to 6 decimals.
 * Pass `exportedAt` to make the output fully reproducible (tests, diffing).
 */
export function serializeLibrary(
  clips: readonly CanonicalAnimationClip[],
  exportedAt: string = new Date().toISOString(),
  folderMeta?: LibraryFolderMeta,
): AnimationLibraryFile {
  const sorted = clips.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  // Absent / empty folder metadata adds no keys at all, so a clip's bytes are unchanged by this
  // feature (`clipFingerprint` in libraryIO compares exactly those bytes).
  const folders = folderMeta ? cleanFolderNames(folderMeta.folders).value : [];
  const clipFolders = folderMeta ? cleanClipFolders(folderMeta.clipFolders).value : {};

  return {
    schemaVersion: LIBRARY_SCHEMA_VERSION,
    rigVersion: RIG_VERSION,
    exportedAt,
    clips: sorted.map((clip) => {
      const tracks: Partial<Record<CanonicalBoneId, BoneRotationTrack>> = {};
      for (const bone of CANONICAL_BONE_IDS) {
        const track = clip.tracks[bone];
        if (!track || track.keyframes.length === 0) continue;
        tracks[bone] = {
          keyframes: track.keyframes
            .slice()
            .sort((a, b) => a.frame - b.frame)
            .map((k) => {
              const q = normalizeQuat(k.rotation);
              return {
                frame: Math.round(k.frame),
                rotation: [round6(q[0]), round6(q[1]), round6(q[2]), round6(q[3])] as QuaternionTuple,
                easing: k.easing,
                ...emitHipsY(bone, k),
              };
            }),
        };
      }
      const events = emitEvents(clip.events);
      return {
        id: clip.id,
        name: clip.name,
        fps: Math.round(clip.fps),
        durationFrames: Math.round(clip.durationFrames),
        loop: clip.loop,
        tracks,
        // Between `tracks` and the timestamps, and omitted when empty: an event-free clip's bytes
        // are identical to what this emitted before events existed.
        ...(events ? { events } : {}),
        createdAt: clip.createdAt,
        updatedAt: clip.updatedAt,
      };
    }),
    ...(folders.length > 0 ? { folders } : {}),
    ...(Object.keys(clipFolders).length > 0 ? { clipFolders } : {}),
  };
}

/**
 * Stable key order, 2-space indent. Same input object → byte-identical output.
 *
 * `minify` drops the indentation for the per-clip / multi-clip downloads, where the file is a
 * payload for a game engine rather than something a human diffs.
 */
export function libraryToJson(file: AnimationLibraryFile, options?: { minify?: boolean }): string {
  const folders = cleanFolderNames(file.folders).value;
  const clipFolders = cleanClipFolders(file.clipFolders).value;
  const ordered = {
    schemaVersion: file.schemaVersion,
    rigVersion: file.rigVersion,
    exportedAt: file.exportedAt,
    clips: file.clips.map((clip) => {
      const tracks: Record<string, { keyframes: RotationKeyframe[] }> = {};
      for (const bone of CANONICAL_BONE_IDS) {
        const track = clip.tracks[bone];
        if (!track || track.keyframes.length === 0) continue;
        tracks[bone] = {
          keyframes: track.keyframes.map((k) => ({
            frame: k.frame,
            rotation: [
              round6(k.rotation[0]),
              round6(k.rotation[1]),
              round6(k.rotation[2]),
              round6(k.rotation[3]),
            ] as QuaternionTuple,
            easing: k.easing,
            ...emitHipsY(bone, k),
          })),
        };
      }
      const events = emitEvents(clip.events);
      return {
        id: clip.id,
        name: clip.name,
        fps: clip.fps,
        durationFrames: clip.durationFrames,
        loop: clip.loop,
        tracks,
        ...(events ? { events } : {}),
        createdAt: clip.createdAt,
        updatedAt: clip.updatedAt,
      };
    }),
    // Emitted last and only when non-empty: an old backup and a new one with no folders in use
    // are byte-identical, and an older reader that knows neither key ignores both.
    ...(folders.length > 0 ? { folders } : {}),
    ...(Object.keys(clipFolders).length > 0
      ? // Sorted so the same assignments always produce the same bytes, whatever order
        // IndexedDB handed the record back in.
        { clipFolders: sortedRecord(clipFolders) }
      : {}),
  };
  return options?.minify === true ? JSON.stringify(ordered) : JSON.stringify(ordered, null, 2);
}

/**
 * The keyframe's `hipsY`, as a spreadable fragment emitted AFTER `easing` — or nothing at all.
 *
 * Shared by both serializers so they cannot drift. Emitted only on the `Hips` track and only when
 * finite, and omitted entirely otherwise: `clipFingerprint` decides whether an exported clip file
 * has gone stale by comparing raw bytes, so a clip that never used the channel must serialize to
 * exactly the bytes it produced before the channel existed.
 */
function emitHipsY(bone: CanonicalBoneId, k: RotationKeyframe): { hipsY?: number } {
  if (bone !== 'Hips') return {};
  return typeof k.hipsY === 'number' && Number.isFinite(k.hipsY) ? { hipsY: round6(k.hipsY) } : {};
}

function sortedRecord(record: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of Object.keys(record).sort()) out[key] = record[key];
  return out;
}

/**
 * Deterministic event emission, shared by `serializeLibrary` and `libraryToJson`.
 *
 * Sorted by frame then name, `data` keys sorted, and `undefined` when there is nothing to write
 * so the key never appears. Both matter to `clipFingerprint`, which compares the raw bytes of one
 * clip to decide whether an exported file has gone stale: a clip with no events must serialize
 * exactly as it did before events existed, and the same `data` written in two key orders must
 * produce the same bytes.
 */
function emitEvents(events: readonly ClipEvent[] | undefined): ClipEvent[] | undefined {
  if (!events || events.length === 0) return undefined;
  const out = events
    .slice()
    .sort((a, b) => (a.frame !== b.frame ? a.frame - b.frame : a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    .map((e) => {
      const entry: ClipEvent = { frame: Math.round(e.frame), name: e.name };
      if (e.data) {
        const keys = Object.keys(e.data).sort();
        if (keys.length > 0) {
          const data: ClipEventData = {};
          for (const key of keys) data[key] = e.data[key];
          entry.data = data;
        }
      }
      return entry;
    });
  return out.length > 0 ? out : undefined;
}

// ---------------------------------------------------------------------------
// Migration hook
// ---------------------------------------------------------------------------

/**
 * Version migration hook. Only schema version 1 exists, so anything else is a compatibility
 * error rather than a silent best-effort import.
 */
export function migrateLibrary(
  raw: Record<string, unknown>,
): { ok: true; raw: Record<string, unknown>; warnings: string[] } | { ok: false; errors: string[] } {
  const version = raw.schemaVersion;
  if (!isInteger(version)) {
    return { ok: false, errors: ['Library "schemaVersion" is missing or is not an integer.'] };
  }
  if (version === LIBRARY_SCHEMA_VERSION) {
    return { ok: true, raw, warnings: [] };
  }
  if (version > LIBRARY_SCHEMA_VERSION) {
    return {
      ok: false,
      errors: [
        `Library schema version ${version} is newer than this build supports ` +
          `(${LIBRARY_SCHEMA_VERSION}). Update Motion Kit to open this file.`,
      ],
    };
  }
  return {
    ok: false,
    errors: [
      `Library schema version ${version} is not supported; no migration to version ` +
        `${LIBRARY_SCHEMA_VERSION} exists.`,
    ],
  };
}

// ---------------------------------------------------------------------------
// Parsing / validation
// ---------------------------------------------------------------------------

/**
 * `bone` is needed because one keyframe field is bone-scoped: `hipsY` exists on the Hips track and
 * nowhere else. A malformed value is an error (a corrupt number would silently displace the
 * character), while the field on the wrong track is only a warning — the file is still usable, the
 * value simply means nothing there, so it is dropped and reported.
 *
 * That warning is emitted ONCE PER TRACK, with a count. A hand-edited library that carries the
 * field on a 3600-key arm track would otherwise hand the caller 3600 identical strings, which is a
 * wall of noise around whatever else the import found. The per-key ERROR is deliberately not
 * collapsed: a non-finite number is corruption, and each occurrence is a separate place to look.
 */
function validateKeyframes(
  raw: unknown,
  where: string,
  bone: CanonicalBoneId,
  errors: string[],
  warnings: string[],
): RotationKeyframe[] | null {
  if (!Array.isArray(raw)) {
    errors.push(`${where}: "keyframes" must be an array.`);
    return null;
  }
  const keys: RotationKeyframe[] = [];
  let previousFrame = -Infinity;
  let failed = false;
  let strayHipsY = 0;

  for (let i = 0; i < raw.length; i++) {
    const entry: unknown = raw[i];
    const at = `${where}[${i}]`;
    if (!isPlainObject(entry)) {
      errors.push(`${at}: keyframe must be an object.`);
      failed = true;
      continue;
    }
    const frame = entry.frame;
    if (!isInteger(frame) || frame < 0) {
      errors.push(`${at}: "frame" must be an integer >= 0 (got ${JSON.stringify(frame)}).`);
      failed = true;
      continue;
    }
    if (frame === previousFrame) {
      errors.push(`${at}: duplicate keyframe at frame ${frame}.`);
      failed = true;
    } else if (frame < previousFrame) {
      errors.push(`${at}: keyframes must be sorted ascending (frame ${frame} follows ${previousFrame}).`);
      failed = true;
    }
    previousFrame = frame;

    const rotation = entry.rotation;
    if (!isValidQuatTuple(rotation)) {
      errors.push(`${at}: "rotation" is not a normalized [x,y,z,w] quaternion.`);
      failed = true;
      continue;
    }

    const easing = entry.easing;
    if (typeof easing !== 'string' || !TWEEN_PRESETS.includes(easing as TweenPreset)) {
      errors.push(`${at}: unknown easing ${JSON.stringify(easing)}.`);
      failed = true;
      continue;
    }

    const key: RotationKeyframe = {
      frame,
      rotation: [rotation[0], rotation[1], rotation[2], rotation[3]],
      easing: easing as TweenPreset,
    };

    const hipsY = entry.hipsY;
    if (hipsY !== undefined) {
      if (typeof hipsY !== 'number' || !Number.isFinite(hipsY)) {
        errors.push(`${at}: "hipsY" must be a finite number.`);
        failed = true;
        continue;
      }
      if (bone === 'Hips') key.hipsY = hipsY;
      else strayHipsY++;
    }

    keys.push(key);
  }

  if (strayHipsY > 0) {
    warnings.push(
      `${where}: ${strayHipsY} keyframe(s) carry "hipsY", which is only meaningful on the Hips track; ignored.`,
    );
  }

  return failed ? null : keys;
}

function validateClip(
  raw: unknown,
  index: number,
  errors: string[],
  warnings: string[],
): CanonicalAnimationClip | null {
  const where = `clips[${index}]`;
  if (!isPlainObject(raw)) {
    errors.push(`${where}: clip must be an object.`);
    return null;
  }

  let failed = false;

  const id = raw.id;
  if (typeof id !== 'string' || id.length === 0) {
    errors.push(`${where}: "id" must be a non-empty string.`);
    failed = true;
  }

  const name = raw.name;
  if (typeof name !== 'string') {
    errors.push(`${where}: "name" must be a string.`);
    failed = true;
  }

  const fps = raw.fps;
  if (!isInteger(fps) || fps < 1 || fps > 240) {
    errors.push(`${where}: "fps" must be an integer between 1 and 240 (got ${JSON.stringify(fps)}).`);
    failed = true;
  }

  const durationFrames = raw.durationFrames;
  if (!isInteger(durationFrames) || durationFrames < 0) {
    errors.push(
      `${where}: "durationFrames" must be an integer >= 0 (got ${JSON.stringify(durationFrames)}).`,
    );
    failed = true;
  }

  let loop: boolean;
  if (raw.loop === undefined) {
    loop = true;
    warnings.push(`${where}: "loop" was missing; defaulting to true.`);
  } else if (typeof raw.loop === 'boolean') {
    loop = raw.loop;
  } else {
    errors.push(`${where}: "loop" must be a boolean.`);
    failed = true;
    loop = true;
  }

  const tracks: Partial<Record<CanonicalBoneId, BoneRotationTrack>> = {};
  const rawTracks = raw.tracks;
  if (!isPlainObject(rawTracks)) {
    errors.push(`${where}: "tracks" must be an object keyed by canonical bone id.`);
    failed = true;
  } else {
    for (const boneKey of Object.keys(rawTracks)) {
      if (!isCanonicalBoneId(boneKey)) {
        errors.push(`${where}.tracks: unknown canonical bone id "${boneKey}".`);
        failed = true;
        continue;
      }
      const rawTrack = rawTracks[boneKey];
      if (!isPlainObject(rawTrack)) {
        errors.push(`${where}.tracks.${boneKey}: track must be an object.`);
        failed = true;
        continue;
      }
      for (const forbidden of ['position', 'scale', 'positions', 'scales']) {
        if (forbidden in rawTrack) {
          errors.push(
            `${where}.tracks.${boneKey}: "${forbidden}" is not allowed — clips are rotation-only.`,
          );
          failed = true;
        }
      }
      const keys = validateKeyframes(
        rawTrack.keyframes,
        `${where}.tracks.${boneKey}.keyframes`,
        boneKey,
        errors,
        warnings,
      );
      if (keys === null) {
        failed = true;
        continue;
      }
      if (keys.length > 0) tracks[boneKey] = { keyframes: keys };
    }
  }

  if (isInteger(durationFrames)) {
    let lastFrame = 0;
    for (const bone of Object.keys(tracks) as CanonicalBoneId[]) {
      const track = tracks[bone];
      if (!track || track.keyframes.length === 0) continue;
      const f = track.keyframes[track.keyframes.length - 1].frame;
      if (f > lastFrame) lastFrame = f;
    }
    if (durationFrames < lastFrame) {
      errors.push(
        `${where}: "durationFrames" (${durationFrames}) is shorter than the last keyframe (${lastFrame}).`,
      );
      failed = true;
    }
  }

  // Events are cosmetic in the same sense folder metadata is: a corrupt marker must never cost the
  // user the clip it is attached to. Bad entries are dropped and counted into `warnings`, and the
  // survivors are clamped by `normalizeEvents` (which is also what keeps a looping clip's event
  // off `durationFrames`, where it would fire twice per loop).
  const events = validateEvents(raw.events, where, warnings);

  const createdAt = typeof raw.createdAt === 'number' && Number.isFinite(raw.createdAt) ? raw.createdAt : null;
  const updatedAt = typeof raw.updatedAt === 'number' && Number.isFinite(raw.updatedAt) ? raw.updatedAt : null;
  if (createdAt === null) warnings.push(`${where}: "createdAt" was missing or invalid; using import time.`);
  if (updatedAt === null) warnings.push(`${where}: "updatedAt" was missing or invalid; using import time.`);

  if (failed) return null;

  const now = Date.now();
  const clip: CanonicalAnimationClip = {
    id: id as string,
    name: name as string,
    fps: fps as number,
    durationFrames: durationFrames as number,
    loop,
    tracks,
    createdAt: createdAt ?? now,
    updatedAt: updatedAt ?? now,
  };

  // Normalized against the clip's OWN duration and loop flag, which are only known now.
  const normalized = normalizeEvents(events, clip);
  if (normalized) clip.events = normalized;

  return clip;
}

/**
 * Drop what cannot be used, count it, and never fail the clip over it. Returns the survivors
 * un-normalized — `validateClip` clamps them once it knows the clip's duration and loop flag.
 */
function validateEvents(raw: unknown, where: string, warnings: string[]): ClipEvent[] {
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) {
    warnings.push(`${where}: "events" was not an array; animation events were ignored.`);
    return [];
  }

  const events: ClipEvent[] = [];
  let dropped = 0;

  for (const entry of raw) {
    if (!isPlainObject(entry) || typeof entry.name !== 'string' || entry.name.trim().length === 0) {
      dropped++;
      continue;
    }
    if (!isInteger(entry.frame) || entry.frame < 0) {
      dropped++;
      continue;
    }

    const event: ClipEvent = { frame: entry.frame, name: entry.name.trim() };

    if (entry.data !== undefined) {
      if (!isPlainObject(entry.data)) {
        // The marker itself is fine; only its payload is not. Keeping the marker and dropping the
        // payload loses less than dropping both.
        warnings.push(`${where}: event "${event.name}" had a "data" that was not an object; it was ignored.`);
      } else {
        const data: ClipEventData = {};
        let badFields = 0;
        for (const key of Object.keys(entry.data)) {
          const value: unknown = (entry.data as Record<string, unknown>)[key];
          if (typeof value === 'string' || typeof value === 'boolean') data[key] = value;
          else if (typeof value === 'number' && Number.isFinite(value)) data[key] = value;
          else badFields++;
        }
        if (badFields > 0) {
          warnings.push(
            `${where}: event "${event.name}" had ${badFields} data field${badFields === 1 ? '' : 's'} ` +
              'that were not a string, finite number or boolean; they were ignored.',
          );
        }
        if (Object.keys(data).length > 0) event.data = data;
      }
    }

    events.push(event);
  }

  if (dropped > 0) {
    warnings.push(
      `${where}: ignored ${dropped} animation event${dropped === 1 ? '' : 's'} without a name or a valid frame.`,
    );
  }
  return events;
}

/**
 * Parse and fully validate a library JSON document. Returns every problem found; a file with
 * any error is rejected outright (no partial import).
 */
export function parseLibraryJson(text: string): LibraryParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    return { ok: false, errors: [`File is not valid JSON: ${(e as Error).message}`] };
  }

  if (!isPlainObject(raw)) {
    return { ok: false, errors: ['Library file must be a JSON object.'] };
  }

  const migrated = migrateLibrary(raw);
  if (!migrated.ok) return { ok: false, errors: migrated.errors };

  const errors: string[] = [];
  const warnings: string[] = [...migrated.warnings];
  const source = migrated.raw;

  if (source.rigVersion !== RIG_VERSION) {
    errors.push(
      source.rigVersion === 'humanoid-v1'
        ? 'This library was exported as "humanoid-v1", where a rotation was measured from each ' +
          `rig's own rest pose. "${RIG_VERSION}" measures it from the shared canonical T-pose so ` +
          'one clip fits every character, and the two cannot be told apart from the numbers ' +
          'alone — importing it would silently shift every pose. Re-export the library from a ' +
          `project opened in this version instead.`
        : `Library rig version ${JSON.stringify(source.rigVersion)} is incompatible with ` +
          `"${RIG_VERSION}".`,
    );
  }

  const exportedAt = typeof source.exportedAt === 'string' ? source.exportedAt : null;
  if (exportedAt === null) warnings.push('"exportedAt" was missing; using import time.');

  const rawClips = source.clips;
  const clips: CanonicalAnimationClip[] = [];
  if (!Array.isArray(rawClips)) {
    errors.push('"clips" must be an array.');
  } else {
    const seen = new Set<string>();
    for (let i = 0; i < rawClips.length; i++) {
      const clip = validateClip(rawClips[i], i, errors, warnings);
      if (!clip) continue;
      if (seen.has(clip.id)) {
        errors.push(`clips[${i}]: duplicate clip id "${clip.id}".`);
        continue;
      }
      seen.add(clip.id);
      clips.push(clip);
    }
  }

  // Folder metadata is additive and cosmetic: a file that predates it parses exactly as before,
  // and a malformed entry is a warning, never a rejected import. Entries pointing at ids that are
  // not in this file are KEPT — the importer decides what to do with them (a copy-mode import
  // rewrites ids, so "unknown here" is not the same as "wrong").
  //
  // ABSENT and EMPTY are different answers, and the fields are preserved as such: `"folders": []`
  // is a file saying "no folders", while no key at all is a file with no opinion about folders.
  // The importer needs to tell those apart — a game-facing clip file carries no folder keys by
  // design, and re-importing one must not un-file the clips it contains.
  const hasFolders = source.folders !== undefined;
  const hasClipFolders = source.clipFolders !== undefined;
  const folders = cleanFolderNames(source.folders);
  const clipFolders = cleanClipFolders(source.clipFolders);
  // A key of the wrong TYPE is ignored, warned about, and does NOT count as an opinion — the
  // file did not manage to say anything about folders.
  const foldersPresent = hasFolders && Array.isArray(source.folders);
  const clipFoldersPresent = hasClipFolders && isPlainObject(source.clipFolders);

  if (hasFolders && !Array.isArray(source.folders)) {
    warnings.push('"folders" was not an array; folder names were ignored.');
  } else if (folders.dropped > 0) {
    warnings.push(`"folders": ignored ${folders.dropped} entr${folders.dropped === 1 ? 'y' : 'ies'} that was not a non-empty string.`);
  }
  if (hasClipFolders && !isPlainObject(source.clipFolders)) {
    warnings.push('"clipFolders" was not an object; folder assignments were ignored.');
  } else if (clipFolders.dropped > 0) {
    warnings.push(
      `"clipFolders": ignored ${clipFolders.dropped} assignment${clipFolders.dropped === 1 ? '' : 's'} that did not name a folder.`,
    );
  }

  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    file: {
      schemaVersion: LIBRARY_SCHEMA_VERSION,
      rigVersion: RIG_VERSION,
      exportedAt: exportedAt ?? new Date().toISOString(),
      clips,
      ...(foldersPresent ? { folders: folders.value } : {}),
      ...(clipFoldersPresent ? { clipFolders: clipFolders.value } : {}),
    },
    warnings,
  };
}

/** Convenience: default easing used when a caller has to invent one. */
export const LIBRARY_DEFAULT_EASING = DEFAULT_EASING;
